import React, { FC, ReactNode, useEffect, useState } from 'react';

import { isBrowser } from '@lucid/utils';
import { LucidTheme, variant, ComponentsType } from '@lucid/theme';
import styled from '@emotion/styled';

import Box from '../box/box';
import Container from '../container/container';
import Link from '../link/link';
import Text from '../text/text';

export interface AlertBannerVariantProps {
  /** The available variants for the Alert Banner Component. */
  variant?: ComponentsType['AlertBanner'];
}

export interface AlertBannerProps extends AlertBannerVariantProps {
  children: ReactNode;
  condition?: boolean;
  /** Hides the Alert Banner when link has been clicked. */
  dismissOnLinkClick?: boolean;
  /** Allows the ability to add a link url to the Alert Component. */
  linkUrl?: string;
  /** Allows the ability to set link text to the Alert Component. */
  linkText?: string;
  /** The storage key to be saved in the browser localStorage. */
  storageKey: string;
  /** Allows the ability to choose localStorage or sessionStorage for the Alert Component. */
  storageType?: 'local' | 'session';
}

const StyledAlertBanner: FC<AlertBannerVariantProps> = styled.div(
  variant({
    scale: 'alert.banner',
    variants: {
      defaultVariant: {}
    }
  }),
  ({ theme }) => (
    {
      label: 'alertBanner',
      position: 'sticky',
      bottom: '0',
      left: '0',
      right: '0',
      width: '100%',
      padding: theme.space[3],
      zIndex: theme.zIndices[3]
    }
  )
);

const StyledAlertBannerButton: FC<AlertBannerVariantProps> = styled.button(
  variant({
    scale: 'alert.button',
    variants: {
      defaultVariant: {}
    }
  }),
  ({ theme }) => (
    {
      label: 'alertBannerButton',
      background: 'none',
      cursor: 'pointer',
      height: '35px',
      width: '35px',
      fontSize: theme.fontSizes[7],
      fontWeight: theme.fontWeights.light,
      lineHeight: 0
    }
  )
);

const AlertBanner: FC<AlertBannerProps> = ({
  children,
  condition = true,
  dismissOnLinkClick = true,
  linkUrl,
  linkText,
  storageKey,
  storageType = 'session',
  variant = 'white'
}) => {
  const [willClose, setWillClose] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const storageValue = 'closed';

  let setItem: () => void;
  let getItem: () => string | undefined | null;

  switch (storageType) {
    case 'local':
      setItem = () => localStorage.setItem(storageKey, storageValue);
      getItem = () => localStorage.getItem(storageKey);
      break;
    case 'session':
      setItem = () => sessionStorage.setItem(storageKey, storageValue);
      getItem = () => sessionStorage.getItem(storageKey);
  }

  useEffect(() => {
    if (isBrowser()) {
      const sessionValue = getItem();
      setWillClose(!!sessionValue);
      if (!sessionValue) {
        setIsVisible(true);
      }
    }
  }, [getItem, storageKey]);

  useEffect(() => {
    if (isBrowser() && willClose) {
      setItem();
    }
  }, [willClose, setItem, storageKey]);

  if (!condition || !isVisible || willClose) {
    return null;
  }

  return (
    <StyledAlertBanner variant={variant}>
      <Container
        displayFlex
        css={{ justifyContent: 'space-between', alignItems: 'center' }}
      >
        <Box
          alignItems="baseline"
          css={(theme: LucidTheme) => ({ marginRight: theme.space[6] })}
          display="flex"
          flexDirection="row"
        >
          <Text
            css={(theme: LucidTheme) => (
              {
                marginRight: theme.space[4],
                marginBottom: 0
              }
            )}
            variant="paragraphS400"
          >
            {children}
          </Text>
          {linkUrl && (
            <Link
              css={(theme: LucidTheme) => ({
                whiteSpace: 'nowrap',
                '&:hover': { color: `${theme.colors.text} !important` }
              })}
              onClick={() => {
                dismissOnLinkClick ? setWillClose(true) : null;
              }}
              to={linkUrl}
              underline="always"
            >
              {linkText || 'Read more.'}
            </Link>
          )}
        </Box>
        <Box
          css={(theme: LucidTheme) => ({ color: theme.colors.primaryText })}
          onClick={() => setWillClose(true)}
        >
          <StyledAlertBannerButton variant={variant}>
            ×
          </StyledAlertBannerButton>
        </Box>
      </Container>
    </StyledAlertBanner>
  );
};

export { AlertBanner };
export default AlertBanner;
